import React, { useCallback, useContext } from 'react';

import { useHistory } from 'react-router-dom';
import { Box, Typography } from '@material-ui/core';
import noop from 'lodash/noop';

import { ActivateSubscriptionDialog } from '@vyce/core/src/components/ActivateSubscriptionDialog';
import { TeamListModuleContext } from '@vyce/core/src/contexts';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { TEAMS_PERMISSIONS } from '@vyce/core/src/types';

import { CreateTeamDialog } from './components';
import { HoldingPage } from '../../components';
import { NO_MODULE_ACCESS_TEXT, NO_TEAM_CREATION_ACCESS_TEXT } from '../../constants';
import config from '../../assets/config';
import { ModuleVideo } from '../../components/ModuleVideo';

interface Props {
  hasPermissions?: boolean;
  hadSubscription?: boolean;
}

export const GetStarted = ({ hasPermissions = true, hadSubscription }: Props) => {
  const {
    needToReset,
    isLoading,
    wasSubscribed,
    isOpenSubscriptionDialog = false,
    openTeamDialog,
    subscribe = noop,
    setSubscriptionDialogOpen = noop,
  } = useContext(TeamListModuleContext);
  const history = useHistory();
  const { can } = usePermissions();

  // Reading the module and creating a team are separate rights, so someone who
  // can see this page may still have nothing to press here.
  const canCreateTeam = can(TEAMS_PERMISSIONS.CAN_CREATE_TEAMS);
  const buttonText = hadSubscription ? 'Reactivate your Teams Module now...' : 'Create Team';
  const textInsteadButton = !hasPermissions
    ? NO_MODULE_ACCESS_TEXT
    : !hadSubscription && !canCreateTeam
    ? NO_TEAM_CREATION_ACCESS_TEXT
    : '';

  const goToSubscription = () => {
    history.push('/subscriptions?dialog=true');
  };

  const handleCreateTeam = useCallback(() => {
    if (hadSubscription) {
      goToSubscription();
    } else {
      openTeamDialog();
    }
  }, [openTeamDialog, hadSubscription]);

  const handleCloseSubscriptionDialog = useCallback(() => {
    setSubscriptionDialogOpen(false);
  }, []);

  return (
    <>
      <HoldingPage
        title="Teams"
        buttonText={buttonText}
        handleButtonClick={handleCreateTeam}
        image={config.welcome.teams}
        textInsteadButton={textInsteadButton}>
        <Box marginBottom={4}>
          <Box marginTop={2} marginBottom={2}>
            <ModuleVideo
              previewUrl={config.previewVideo.teams}
              url="https://youtu.be/nkkUK0nEMZ4?si=nmScIRZJp_i46RZb"
            />
          </Box>

          <Box marginTop={1} display="flex">
            <Typography>
              Onboard, verify and manage your workforce all in one place with Vyce’s easy to use Teams
              Module.
            </Typography>
          </Box>

          <Box marginTop={3}>
            <ul style={{ paddingLeft: 16 }}>
              <li>
                <Typography>Paperless onboarding</Typography>
              </li>
              <li>
                <Typography>Real time workforce intelligence</Typography>
              </li>
              <li>
                <Typography>Better compliance</Typography>
              </li>
              <li>
                <Typography>Happier teams!</Typography>
              </li>
            </ul>
          </Box>

          <Box marginTop={3}>
            <Typography>
              {canCreateTeam
                ? 'Simply create a new Team to get started.'
                : 'Teams created by your company will appear here.'}
            </Typography>
          </Box>
        </Box>
      </HoldingPage>

      <CreateTeamDialog />

      <ActivateSubscriptionDialog
        callback={subscribe}
        open={isOpenSubscriptionDialog}
        onClose={handleCloseSubscriptionDialog}
        loading={isLoading}
        needToReset={needToReset}
        wasSubscribed={wasSubscribed}
      />
    </>
  );
};
